import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MiChip } from "@miplatform/components";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IVerticalTabProps } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { IItemData } from "../Items.types";
import { addItemsSchema } from "../schema/addItems.schema";
import { ItemCostPricingTab } from "../tabs/ItemCostPricingTab/ItemCostPricingTab";
import { ItemDetailTab } from "../tabs/ItemDetailTab/ItemDetailTab";
import { ItemLinkItemTab } from "../tabs/ItemLinkItemTab/ItemLinkItemTab";
import { ItemPreviewTab } from "../tabs/ItemPreviewTab/ItemPreviewTab";
import { useAddItemViewModel } from "./useAddItemViewModel";

export function AddItems() {
  const [selectedTab, setSelectedTab] = useState("details");

  const { params, goBack } = useRouter();
  const { id: itemId } = params;
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const isReadOnly = !hasPermission(PERMISSIONS.MATERIAL.UPDATE);

  const form = useForm<IItemData>({
    mode: "onChange",
    defaultValues: {
      id: createUUID(),
      category: {
        categoryName: "",
        categoryId: "",
      },
      manufacturerPartNumber: "",
      manufacturerDescription: "",
      fieldNote: "",
      manufacturerListPrice: 0,
      taxRate: 0,
      cost: 0,
      linkedMaterials: [],
      attributes: [],
    },
    resolver: zodResolver(addItemsSchema),
  });

  const {
    formState: { isValid, isDirty },
    reset,
    getValues,
    watch,
  } = form;

  const {
    category: { itemDescription, categoryId, imageRequired, categoryName },
    manufacturerPartNumber,
    manufacturerListPrice,
    linkedMaterials = [],
    document,
    attributes,
    taxRate,
  } = watch();

  const { isLoading, handleCreateItem, handleUpdateItem, fetchItemDetails } =
    useAddItemViewModel({ reset, goBack });

  useEffect(() => {
    if (itemId) {
      fetchItemDetails(itemId);
    }
  }, []);

  const isDetailsTabComplete =
    categoryId &&
    manufacturerPartNumber &&
    itemDescription &&
    (!imageRequired || document) &&
    (!attributes || attributes.every((attr) => !attr.isRequired || attr.value));
  const isCostTabComplete = manufacturerListPrice && taxRate;
  const isLinkedItemsTabComplete = linkedMaterials.length > 0;

  const tabs = useMemo<IVerticalTabProps<IItemData>[]>(
    () => [
      {
        label: "Details",
        value: "details",
        controlledFormViewComponent: ItemDetailTab,
        isComplete: !!isDetailsTabComplete,
        hasErrors: !isDetailsTabComplete,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Cost Pricing",
        value: "cost pricing",
        controlledFormViewComponent: ItemCostPricingTab,
        isComplete: !!isCostTabComplete,
        hasErrors: !isCostTabComplete,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Linked Items",
        value: "linked items",
        controlledFormViewComponent: ItemLinkItemTab,
        isComplete: isLinkedItemsTabComplete,
        extraTabProps: { isReadOnly, setSelectedTab },
      },
      {
        label: "Preview",
        value: "preview",
        controlledFormViewComponent: ItemPreviewTab,
        extraTabProps: { isReadOnly },
      },
    ],
    [isValid, isDetailsTabComplete, isCostTabComplete, isLinkedItemsTabComplete]
  );

  const handleSubmit = () => {
    const data = getValues();
    if (itemId) {
      handleUpdateItem(data);
    } else {
      handleCreateItem(data);
    }
  };

  const renderTitle = () => {
    const action = itemId ? "Edit" : "Add";
    const showSeparator = manufacturerPartNumber && itemDescription;
    return (
      <>
        {action} Item:{" "}
        <span style={{ color: theme.palette.primary.main }}>
          {manufacturerPartNumber}
          {showSeparator ? " | " : ""}
          {itemDescription}
        </span>{" "}
        {categoryName && (
          <MiChip
            label={categoryName}
            size="small"
            sx={{
              borderRadius: 1,
              backgroundColor: theme.palette.primary.light,
              border: `1px solid ${theme.palette.primary.accent}`,
              color: theme.palette.primary.accent,
              height: 16,
            }}
          />
        )}
      </>
    );
  };

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        goBack();
      },
    },
    submit: {
      label: itemId ? "Update Item" : "Save Item",
      onClick: () => {
        handleSubmit();
      },
      disabled: !isValid || !isDirty || isLoading,
    },
  };

  const isLinkedItemsTab = selectedTab === "linked items";
  const contentStyles = isLinkedItemsTab ? { overflow: "hidden" } : {};
  const layoutStyle = isLinkedItemsTab ? { margin: 0, gap: 0 } : {};

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      bottomActions={actions}
      loading={isLoading}
    >
      <VerticalTabsLayout
        tabs={tabs}
        form={form}
        transparentBackground
        hideNavigationButtons={isLinkedItemsTab}
        value={selectedTab}
        setValue={(tab) => setSelectedTab(tab as string)}
        contentStyles={contentStyles}
        layoutStyle={layoutStyle}
      />
    </ConfigPageLayout>
  );
}
